/**
 * MotionSection — Framer Motion scroll-reveal wrapper for marketing sections.
 * Replaces SectionReveal for new homepage sections. Respects prefers-reduced-motion.
 */

'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils/utils';
import { fadeUp, mktgEase } from '@/components/marketing/motion-variants';
import { useEntrance } from '@/components/marketing/use-entrance';
import type { Variants, Transition } from 'framer-motion';

interface MotionSectionProps {
  children: React.ReactNode;
  variants?: Variants;
  transition?: Transition;
  className?: string;
  /** Animate only on first scroll into view (default true) */
  once?: boolean;
  /** Fraction of element visible before triggering (default 0.2) */
  amount?: number;
  style?: React.CSSProperties;
}

export function MotionSection({
  children,
  variants = fadeUp,
  transition = mktgEase,
  className,
  once = true,
  amount = 0.2,
  style
}: MotionSectionProps) {
  // SSR-visible entrance: server HTML renders the visible state (agents and
  // no-JS readers get full content); the hidden state + scroll reveal are
  // applied client-side. Reduced motion keeps the section visible throughout.
  const { ref, entrance, prefersReduced } = useEntrance<HTMLDivElement>({
    once,
    amount
  });

  // Skip animations entirely when user prefers reduced motion
  if (prefersReduced) {
    return (
      <div ref={ref} className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={false}
      animate={entrance}
      variants={variants}
      transition={transition}
      className={cn(className)}
      style={style}
    >
      {children}
    </motion.div>
  );
}
